// counter — número que conta até o valor ("87%", "R$ 2.000", "3x"), barra de destaque
// crescendo junto e um pop quando o número assenta.
import React from 'react';
import { interpolate } from 'remotion';
import type { CounterProps } from './schema';
import { formatGraphicNumber } from './schema';
import { CARD, EASE_OUT, FONT, MUTED, Pop, SAFE_W, alpha, fitFont, prog, textWidth, useGFrame, type GraphicComponentProps } from './common';

export const Counter: React.FC<GraphicComponentProps<CounterProps>> = ({ props, totalFrames, accent }) => {
  const f = useGFrame();
  const { value, prefix = '', suffix = '', label = '', decimals = 0 } = props;
  const land = Math.max(14, Math.min(30, Math.round(totalFrames * 0.5)));
  const p = prog(f, 3, land, EASE_OUT);
  const shown = formatGraphicNumber(value * p, decimals);
  const finalText = formatGraphicNumber(value, decimals);

  // prefixo/sufixo a 55% do número; o conjunto cabe na largura segura
  const padX = 64;
  const whole = `${prefix}${finalText}${suffix}`;
  const size = fitFont(whole, { weight: 900, max: 200, min: 90, maxWidth: SAFE_W - padX * 2 - 20 });
  const small = Math.round(size * 0.55);
  const numW = textWidth(finalText, size, 900) + 8; // largura travada: o número não "respira" contando
  const bump = interpolate(f, [land, land + 4, land + 10], [1, 1.07, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const labelIn = prog(f, 7, 15, EASE_OUT);
  const labelSize = label ? fitFont(label, { weight: 600, max: 44, min: 32, maxWidth: SAFE_W - padX * 2 }) : 0;

  return (
    <div style={{ ...CARD, padding: `30px ${padX}px 38px`, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: SAFE_W }}>
      <Pop at={land} />
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          lineHeight: 1,
          fontFamily: FONT,
          fontWeight: 900,
          letterSpacing: -2,
          scale: String(bump),
          whiteSpace: 'nowrap',
        }}
      >
        {prefix ? <span style={{ fontSize: small, color: accent, marginRight: 10, fontWeight: 800 }}>{prefix}</span> : null}
        <span style={{ fontSize: size, color: '#fff', width: numW, textAlign: prefix && !suffix ? 'left' : suffix && !prefix ? 'right' : 'center', display: 'inline-block' }}>{shown}</span>
        {suffix ? <span style={{ fontSize: small, color: accent, marginLeft: 6, fontWeight: 800 }}>{suffix}</span> : null}
      </div>
      <div style={{ width: '100%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,0.08)', marginTop: 18, overflow: 'hidden' }}>
        <div style={{ width: `${(p * 100).toFixed(2)}%`, height: '100%', borderRadius: 6, background: `linear-gradient(90deg, ${alpha(accent, 0.7)}, ${accent})`, boxShadow: `0 0 18px ${alpha(accent, 0.6)}` }} />
      </div>
      {label ? (
        <div
          style={{
            marginTop: 20,
            fontWeight: 600,
            fontSize: labelSize,
            color: MUTED,
            opacity: labelIn,
            filter: `blur(${((1 - labelIn) * 8).toFixed(2)}px)`,
            translate: `0px ${((1 - labelIn) * 12).toFixed(1)}px`,
            textAlign: 'center',
            lineHeight: 1.15,
          }}
        >
          {label}
        </div>
      ) : null}
    </div>
  );
};
